// Writing a ZIP, stored rather than deflated.
//
// Photos and video are already compressed; deflating them again spends CPU to
// grow the file by a fraction of a percent. The transcript is the only thing
// that would gain, and it is kilobytes.
//
// Nothing is held in memory that does not have to be. Each member's bytes stay
// in the Blob the Service Worker handed us — read once to checksum, then
// referenced, never copied — and the result is a Blob of parts the browser
// spills to disk on its own.

const LOC = 0x04034b50, CEN = 0x02014b50, EOCD = 0x06054b50;

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

/** Streamed, because a member can be a 200 MB video. */
async function crc32(blob: Blob): Promise<number> {
  let c = 0xffffffff;
  const reader = blob.stream().getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    for (let i = 0; i < value.length; i++) c = CRC_TABLE[(c ^ value[i]!) & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

/** DOS time, which is what a zip stores and what every tool expects to read. */
function dosStamp(d: Date) {
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  const date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return { time, date };
}

export interface ZipInput { name: string; blob: Blob; at?: Date }

/**
 * No zip64. A single export past 4 GiB would need it, and refusing loudly
 * beats writing an archive that some tools open and others call corrupt.
 */
export const ZIP_LIMIT = 0xffffffff;

export async function writeZip(files: ZipInput[]): Promise<Blob> {
  const enc = new TextEncoder();
  const parts: BlobPart[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const f of files) {
    const name = enc.encode(f.name);
    const size = f.blob.size;
    const crc = await crc32(f.blob);
    const { time, date } = dosStamp(f.at ?? new Date());

    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, LOC, true);
    lv.setUint16(4, 20, true);          // version needed
    lv.setUint16(6, 0x0800, true);      // UTF-8 names
    lv.setUint16(8, 0, true);           // stored
    lv.setUint16(10, time, true); lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true); lv.setUint32(22, size, true);
    lv.setUint16(26, name.length, true);
    local.set(name, 30);

    const cen = new Uint8Array(46 + name.length);
    const cv = new DataView(cen.buffer);
    cv.setUint32(0, CEN, true);
    cv.setUint16(4, 20, true); cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true); cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true); cv.setUint32(24, size, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    cen.set(name, 46);

    parts.push(local as BlobPart, f.blob);
    central.push(cen);
    offset += local.length + size;
    if (offset > ZIP_LIMIT) throw new Error('This conversation is too large to export as one file.');
  }

  const cdSize = central.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, EOCD, true);
  ev.setUint16(8, files.length, true); ev.setUint16(10, files.length, true);
  ev.setUint32(12, cdSize, true); ev.setUint32(16, offset, true);

  // The cast is the Blob type's own imprecision about Uint8Array backing
  // stores, not a claim about these bytes.
  return new Blob([...parts, ...central, end] as BlobPart[], { type: 'application/zip' });
}
